import type { GroupCount } from '@/api/analytics'

const BAR_COLORS = [
  'bg-brand', 'bg-blue-500', 'bg-purple-500', 'bg-teal-500', 'bg-amber-500',
  'bg-indigo-500', 'bg-green-500', 'bg-pink-500', 'bg-gray-400',
]

/** Horizontal bar-list for "Head Count By X" widgets: label, count, and a
 * proportional colored bar, sorted largest-first. Pass `onSelect` to make
 * each row clickable (e.g. to drill into who's behind that count) — omit it
 * and rows render exactly as before, inert. */
export default function BarListWidget({ data, onSelect }: { data: GroupCount[]; onSelect?: (label: string) => void }) {
  if (!data || data.length === 0) {
    return <EmptyState />
  }
  const sorted = [...data].sort((a, b) => b.count - a.count)
  const total = sorted.reduce((sum, d) => sum + d.count, 0) || 1
  const max = Math.max(...sorted.map((d) => d.count), 1)

  return (
    <div className="space-y-4">
      {sorted.map((row, i) => {
        const pct = Math.round((row.count / total) * 1000) / 10
        const widthPct = Math.max(2, Math.round((row.count / max) * 100))
        const Row = onSelect ? 'button' : 'div'
        return (
          <Row
            key={row.label}
            type={onSelect ? 'button' : undefined}
            onClick={onSelect ? () => onSelect(row.label) : undefined}
            className={`block w-full text-left ${onSelect ? 'cursor-pointer group' : ''}`}
          >
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className={`text-sm text-gray-700 truncate ${onSelect ? 'group-hover:text-brand transition-colors' : ''}`}>{row.label}</span>
              <span className="text-sm font-semibold text-gray-900 shrink-0">
                {row.count} <span className="text-gray-400 font-normal">({pct}%)</span>
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${BAR_COLORS[i % BAR_COLORS.length]}`}
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </Row>
        )
      })}
    </div>
  )
}

export function EmptyState({ label = 'No Record Found!' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <svg viewBox="0 0 48 48" className="h-12 w-12 text-gray-300 mb-2" fill="none">
        <path d="M10 6h20l8 8v28a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" />
        <path d="M30 6v8h8" stroke="currentColor" strokeWidth="2" />
        <path d="M16 26h4m8 0h4M16 32h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="34" cy="34" r="7" fill="white" stroke="currentColor" strokeWidth="2" />
        <path d="M34 31v4M34 38h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  )
}
